/* tutor-2 (C.1): LA MECANICA DEL CHAT DEL TUTOR, UNA SOLA PIEZA para la pagina (`pages/TutorPage.js`) y la tarjeta
   flotante de a media sesion (`components/tutor/TutorFlotante.js`).

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Lo que vive aqui: la conversacion (que se crea EN EL PRIMER MENSAJE o al subir el primer archivo, con lo que diga
   `cuerpoAlCrear()`), los mensajes, el flujo SSE del mensaje (estado, texto, fuentes, chips, fin, titulo, error),
   "Detener", la opinion, el cuadro (texto y fotos elegidas) y la subida de archivos por SSE (el chip "Leyendo
   archivo.pdf..." mientras no llega `fin`). Lo que NO vive aqui: la lista de conversaciones, el panel de elegir
   documentos, renombrar y borrar (solo la pagina los tiene), y como se pinta (cada envoltura lo suyo).

   Cada subida tiene su `AbortController`; al desmontar se cortan (el servidor cancela y limpia lo suyo).

   tutor-3 (B.1, P1): con `recordar` (SOLO la pagina lo enciende; la tarjeta de a media sesion no) el texto del cuadro
   se guarda en el aparato como BORRADOR de su conversacion (`lib/recuerdoDelTutor.js`): al escribir, con un retardo
   corto, y al desmontar; `cargar` repone el de esa conversacion y `reiniciar` el de la nueva; al crearse la
   conversacion el de la nueva pasa a ella; al mandar se borra (y vuelve si el mensaje falla). */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { avisarSinRed } from '../components/alumno/sinRed';
import { redimensionarFoto } from './fotosAlumno';
import { refrescarTutor } from './tutorDeLaSesion';
import { borrarBorrador, guardarBorrador, leerBorrador } from './recuerdoDelTutor';
import {
  abrirConversacion, codigoDelError, crearConversacion, ErrorDelTutor, mandarMensaje, opinar, quitarArchivo,
  subirArchivo,
} from './tutor';

export const TOPE_FOTOS = 10;
/** tutor-3 (B.1): el retardo del guardado del borrador mientras se escribe. */
export const ESPERA_DEL_BORRADOR_MS = 400;

let siguienteId = 0;
export const idLocal = (p) => `${p}-${Date.now()}-${(siguienteId += 1)}`;

export default function useConversacionDelTutor({
  user, sinRed = false, cuerpoAlCrear, despuesDeMandar, recordar = false,
} = {}) {
  const { t, i18n } = useTranslation(['study', 'common']);
  const [conv, setConv] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [valor, setValorCrudo] = useState('');
  const [fotos, setFotos] = useState([]);
  const [contestando, setContestando] = useState(false);
  const [estadoVivo, setEstadoVivo] = useState(null);
  const [vivo, setVivo] = useState(null);
  const [archivoGrande, setArchivoGrande] = useState(null);
  const [subiendo, setSubiendo] = useState([]);
  const controladorRef = useRef(null);
  const subidasRef = useRef(new Map());
  const cuerpoRef = useRef(cuerpoAlCrear);
  cuerpoRef.current = cuerpoAlCrear;
  const despuesRef = useRef(despuesDeMandar);
  despuesRef.current = despuesDeMandar;
  const convRef = useRef(null);
  convRef.current = conv;
  const creandoRef = useRef(null);

  // ------------------------------------------------------------------ tutor-3 (B.1): el borrador del cuadro
  const uid = recordar && user && user.id ? user.id : null;
  const uidRef = useRef(uid);
  uidRef.current = uid;
  const pendienteRef = useRef(null);
  const guardarYa = useCallback(() => {
    const p = pendienteRef.current;
    if (!p) return;
    pendienteRef.current = null;
    clearTimeout(p.reloj);
    guardarBorrador(p.uid, p.convId, p.texto);
  }, []);
  const setValor = useCallback((v) => {
    setValorCrudo(v);
    const quien = uidRef.current;
    if (!quien || typeof v === 'function') return;
    const convId = convRef.current ? convRef.current.id : null;
    if (pendienteRef.current && pendienteRef.current.convId !== convId) guardarYa();
    if (pendienteRef.current) clearTimeout(pendienteRef.current.reloj);
    const p = { uid: quien, convId, texto: v };
    p.reloj = setTimeout(guardarYa, ESPERA_DEL_BORRADOR_MS);
    pendienteRef.current = p;
  }, [guardarYa]);
  const reponer = useCallback((convId) => {
    guardarYa();
    if (uidRef.current) setValorCrudo(leerBorrador(uidRef.current, convId));
  }, [guardarYa]);

  useEffect(() => () => {
    guardarYa();
    subidasRef.current.forEach((c) => { try { c.abort(); } catch (e) { /* nada */ } });
    subidasRef.current.clear();
  }, [guardarYa]);

  const errorDe = useCallback((codigo) => {
    toast.error(t(`common:errors.${codigo || 'tutor_failed'}`, { defaultValue: t('study:tutor.error.generic') }));
  }, [t]);

  /** La conversacion de ahora; si no hay, la crea (una sola vez aunque dos cosas la pidan a la vez). */
  const asegurarConversacion = useCallback(async () => {
    if (convRef.current) return convRef.current;
    if (creandoRef.current) return creandoRef.current;
    const cuerpo = (cuerpoRef.current && cuerpoRef.current()) || {};
    creandoRef.current = crearConversacion(cuerpo).then((nueva) => {
      if (uidRef.current) {
        // B.1: el borrador de la conversacion nueva pasa a la que acaba de nacer
        guardarYa();
        const texto = leerBorrador(uidRef.current, null);
        borrarBorrador(uidRef.current, null);
        if (texto) guardarBorrador(uidRef.current, nueva.id, texto);
      }
      convRef.current = nueva;
      setConv(nueva);
      setMensajes([]);
      return nueva;
    }).finally(() => { creandoRef.current = null; });
    return creandoRef.current;
  }, [guardarYa]);

  /** Abre una conversacion guardada (la lista, `?conv=`). Devuelve la conversacion o levanta. */
  const cargar = useCallback(async (id) => {
    const c = await abrirConversacion(id);
    reponer(c.id);
    convRef.current = c;
    setConv(c);
    setMensajes(c.mensajes_lista || []);
    setVivo(null);
    return c;
  }, [reponer]);

  /** Vuelve a la conversacion nueva (vacia). */
  const reiniciar = useCallback(() => {
    reponer(null);
    convRef.current = null;
    setConv(null);
    setMensajes([]);
    setVivo(null);
  }, [reponer]);

  const mandar = useCallback(async (textoLibre) => {
    const texto = String(textoLibre != null ? textoLibre : valor).trim();
    if (!texto || contestando) return;
    if (sinRed) { avisarSinRed(t); return; }
    const fotosDelMensaje = fotos;
    let c;
    try {
      c = await asegurarConversacion();
    } catch (e) {
      errorDe(codigoDelError(e));
      return;
    }
    const local = { id: idLocal('alumno'), rol: 'alumno', texto, fotosLocales: fotosDelMensaje.map((f) => f.url) };
    setMensajes((m) => [...m, local]);
    setValorCrudo('');
    setFotos([]);
    setContestando(true);
    setEstadoVivo({ tipo: 'pensando', doc: null });
    const acumulado = { texto: '', fuentes: [], chips: [], tema: null, tema_documento: null };
    setVivo({ ...acumulado });
    const controlador = new AbortController();
    controladorRef.current = controlador;
    let fin = null;
    let fallo = null;
    if (uidRef.current) {
      // B.1: el borrador de ese mensaje se borra al mandarlo (y vuelve con `deshacer` si falla)
      if (pendienteRef.current) { clearTimeout(pendienteRef.current.reloj); pendienteRef.current = null; }
      borrarBorrador(uidRef.current, c.id);
      borrarBorrador(uidRef.current, null);
    }
    const deshacer = () => {
      setMensajes((m) => m.filter((x) => x.id !== local.id));
      setValorCrudo(texto);
      if (uidRef.current) guardarBorrador(uidRef.current, c.id, texto);
      setFotos(fotosDelMensaje);
    };
    try {
      await mandarMensaje({
        convId: c.id, texto, fotos: fotosDelMensaje.map((f) => f.file), lang: i18n.language, signal: controlador.signal,
        alEvento: ({ evento, datos }) => {
          if (evento === 'estado') {
            if (!acumulado.texto) setEstadoVivo(datos);
          } else if (evento === 'texto') {
            acumulado.texto += (datos && datos.delta) || '';
            setEstadoVivo(null);
            setVivo({ ...acumulado });
          } else if (evento === 'fuentes') {
            acumulado.fuentes = (datos && datos.fuentes) || [];
          } else if (evento === 'chips') {
            acumulado.chips = (datos && datos.chips) || [];
            acumulado.tema = (datos && datos.tema) || null;
            acumulado.tema_documento = (datos && datos.tema_documento) || null;
          } else if (evento === 'fin') {
            fin = datos || {};
            setMensajes((m) => [...m, {
              id: fin.mensaje_id, rol: 'tutor', texto: acumulado.texto, fuentes: acumulado.fuentes,
              chips: acumulado.chips, tema: acumulado.tema, tema_documento: acumulado.tema_documento, opinion: null,
              detenido: Boolean(fin.detenido),
            }]);
            setVivo(null);
            setEstadoVivo(null);
            setConv((x) => (x ? { ...x, recortados: fin.recortados || [] } : x));
          } else if (evento === 'titulo') {
            const titulo = datos && datos.titulo;
            if (titulo) setConv((x) => (x ? { ...x, titulo } : x));
          } else if (evento === 'error') {
            fallo = (datos && datos.codigo) || 'tutor_failed';
          }
        },
      });
    } catch (e) {
      if (controlador.signal.aborted) {
        fallo = null;
      } else {
        fallo = e instanceof ErrorDelTutor ? e.codigo : 'tutor_failed';
      }
    }
    controladorRef.current = null;
    setContestando(false);
    setEstadoVivo(null);
    setVivo(null);
    if (controlador.signal.aborted && !fin) {
      // Detener: el servidor guardo lo que llevaba; se vuelve a leer la conversacion para quedar igual que el.
      try {
        const c2 = await abrirConversacion(c.id);
        setConv(c2);
        setMensajes(c2.mensajes_lista || []);
      } catch (e) {
        /* se queda lo local */
      }
    } else if (fallo && !fin) {
      deshacer();
      errorDe(fallo);
    } else if (fallo) {
      errorDe(fallo);
    }
    if (user) refrescarTutor(user);
    if (despuesRef.current) despuesRef.current();
  }, [valor, contestando, sinRed, fotos, asegurarConversacion, errorDe, i18n.language, t, user]);

  /** B.1: olvida el borrador de una conversacion (al borrarla). */
  const olvidarBorrador = useCallback((id) => {
    if (uidRef.current) borrarBorrador(uidRef.current, id);
  }, []);

  const detener = useCallback(() => {
    if (controladorRef.current) controladorRef.current.abort();
  }, []);

  const elegirFotos = useCallback(async (archivos) => {
    const lugar = TOPE_FOTOS - fotos.length;
    if (archivos.length > lugar) toast.error(t('study:tutor.photos.max'));
    const tomadas = archivos.slice(0, Math.max(0, lugar));
    const listas = [];
    for (const f of tomadas) {
      const chica = await redimensionarFoto(f);
      listas.push({ id: idLocal('foto'), file: chica, url: URL.createObjectURL(chica) });
    }
    if (listas.length) setFotos((x) => [...x, ...listas].slice(0, TOPE_FOTOS));
  }, [fotos.length, t]);

  const quitarFoto = useCallback((id) => {
    setFotos((x) => {
      const f = x.find((y) => y.id === id);
      if (f) { try { URL.revokeObjectURL(f.url); } catch (e) { /* nada */ } }
      return x.filter((y) => y.id !== id);
    });
  }, []);

  const elegirArchivo = useCallback(async (archivo) => {
    if (sinRed) { avisarSinRed(t); return; }
    const marca = { id: idLocal('subida'), nombre: (archivo && archivo.name) || '', pagina: 0, total: 0 };
    const controlador = new AbortController();
    subidasRef.current.set(marca.id, controlador);
    try {
      const c = await asegurarConversacion();
      setSubiendo((s) => [...s, marca]);
      const subido = await subirArchivo(c.id, archivo, {
        signal: controlador.signal,
        alEstado: (e) => setSubiendo((s) => s.map((x) => (x.id === marca.id
          ? { ...x, pagina: Number(e.pagina) || 0, total: Number(e.total) || 0 } : x))),
      });
      setConv((x) => (x ? { ...x, archivos: [...(x.archivos || []), subido] } : x));
    } catch (e) {
      if (!controlador.signal.aborted) {
        const codigo = e instanceof ErrorDelTutor ? e.codigo : codigoDelError(e);
        if (codigo === 'tutor_file_too_big') setArchivoGrande(archivo);
        else errorDe(codigo);
      }
    } finally {
      subidasRef.current.delete(marca.id);
      setSubiendo((s) => s.filter((x) => x.id !== marca.id));
    }
  }, [sinRed, t, asegurarConversacion, errorDe]);

  const quitarArchivoDeLaConversacion = useCallback(async (id) => {
    if (!conv) return;
    try {
      await quitarArchivo(conv.id, id);
      setConv((x) => (x ? { ...x, archivos: (x.archivos || []).filter((a) => a.id !== id) } : x));
    } catch (e) {
      errorDe(codigoDelError(e));
    }
  }, [conv, errorDe]);

  const opinarDe = useCallback(async (mensaje, valorOpinion) => {
    if (!conv) return;
    setMensajes((m) => m.map((x) => (x.id === mensaje.id ? { ...x, opinion: valorOpinion } : x)));
    try {
      await opinar(conv.id, mensaje.id, valorOpinion);
    } catch (e) {
      setMensajes((m) => m.map((x) => (x.id === mensaje.id ? { ...x, opinion: mensaje.opinion } : x)));
    }
  }, [conv]);

  return {
    conv, setConv, mensajes, setMensajes, valor, setValor, fotos, contestando, estadoVivo, vivo, archivoGrande,
    setArchivoGrande, subiendo, errorDe, asegurarConversacion, cargar, reiniciar, mandar, detener, elegirFotos,
    quitarFoto, elegirArchivo, quitarArchivoDeLaConversacion, opinarDe, olvidarBorrador,
  };
}
